import React, { useState, useEffect } from 'react';
import ProductCard from '../components/ProductCard/ProductCard';
import { API_ENDPOINTS } from '../config/apiConfig';

import { Product,Option, Dates } from '../components/ProductCard/ProductCard'; // Import the Product type

type AvailabilitySlot = {
  id: string;
  date: string; // ISO format
  startTime: string; // HH:MM format
  endTime: string; // HH:MM format
};

const PriseRdvPage: React.FC = () => {
  // State for products and selected product
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  
  // State for availability slots
  const [availability, setAvailability] = useState<AvailabilitySlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<AvailabilitySlot | null>(null);
  
  // Loading state
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for dropdown selections
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');

  // State for contact information
  const [email, setEmail] = useState<string>('');
  const [telephone, setTelephone] = useState<string>('');
  const [commentaire, setCommentaire] = useState<string>('');
  const [participantsAmount, setParticipantsAmount] = useState<number>(1);

  // Fetch products from backend
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.LOADRESERVATION);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        
        setProducts(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Erreur lors du chargement des produits');
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Simulate fetching availability from backend (this would also be replaced with an API call)
  useEffect(() => {
    if (!selectedProduct) return;

    const fetchAvailability = async () => {
      try {
        // In a real app, this would be an API call:
        // const response = await fetch(`/api/availability/${selectedProduct.id}`);
        // const data = await response.json();
        
        // For now, we'll use mock availability
        let mockAvailability: AvailabilitySlot[] = [];
        if (selectedProduct.duration.includes('h')) {
          // Generate 30-min slots for the next 60 days (2 months)
          const today = new Date();
          const maxDays = 60; // Only show dates within 2 months
          
          for (let i = 0; i < maxDays; i++) {
            const day = new Date(today);
            day.setDate(today.getDate() + i);
            
            // Only include days that are not in the past
            if (day >= today) {
              const dateStr = day.toISOString().split('T')[0];
              // Generate slots from 9:00 to 18:00 with 30-min intervals
              for (let hour = 9; hour < 18; hour++) {
                for (let minute = 0; minute < 60; minute += 30) {
                  if (minute === 30) continue; // Skip 30-minute marks to avoid duplicates
                  
                  const startTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                  let endTime = '';
                  
                  if (minute === 0) {
                    // 9:00 -> 9:30
                    endTime = `${hour.toString().padStart(2, '0')}:${(minute + 30).toString().padStart(2, '0')}`;
                  } else {
                    // 9:30 -> 10:00 (when hour is 9)
                    const nextHour = hour + 1;
                    endTime = `${nextHour.toString().padStart(2, '0')}:00`;
                  }
                  
                  mockAvailability.push({
                    id: `${dateStr}-${startTime}`,
                    date: dateStr,
                    startTime,
                    endTime
                  });
                }
              }
            }
          }
        } else {
          // For days, just generate some sample dates within the next 60 days
          const today = new Date();
          const maxDays = 60; // Only show dates within 2 months
          
          for (let i = 1; i <= maxDays; i++) {
            const day = new Date(today);
            day.setDate(today.getDate() + i);
            
            // Only include days that are not in the past
            if (day >= today) {
              const dateStr = day.toISOString().split('T')[0];
              mockAvailability.push({
                id: `${dateStr}-full-day`,
                date: dateStr,
                startTime: '09:00',
                endTime: '18:00'
              });
            }
          }
        }
        
        setAvailability(mockAvailability);
      } catch (err) {
        setError('Erreur lors du chargement des disponibilités');
      }
    };

    fetchAvailability();
  }, [selectedProduct]);

  // Handle product selection
  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setSelectedSlot(null);
    setSelectedDate('');
    setSelectedTime('');
  };

  // Handle slot selection
  const handleSelectSlot = (slot: AvailabilitySlot) => {
    setSelectedSlot(slot);
  };

  // Filter availability by selected date
  const filteredAvailabilityByDate = availability.filter(slot => slot.date === selectedDate);

  // Group availability by date for dropdown
  const availableDates = Array.from(new Set(availability.map(slot => slot.date)))
    .sort()
    .filter(date => {
      const dateObj = new Date(date);
      // Only show dates from today onwards (no past dates)
      if (dateObj < new Date()) return false;
      
      // If the product has date constraints, filter by those
      if (selectedProduct && selectedProduct.hasOwnProperty('dates')) {
        const productDateStart = selectedProduct.dates?.[0].dateStart ? new Date(selectedProduct.dates?.[0].dateStart) : null;
        const productDateEnd = selectedProduct.dates?.[0].dateEnd ? new Date(selectedProduct.dates?.[0].dateEnd) : null;
        
        // If dateStart exists, ensure date is not before it
        if (productDateStart && dateObj < productDateStart) return false;
        
        // If dateEnd exists, ensure date is not after it
        if (productDateEnd && dateObj > productDateEnd) return false;
      }
      
      return true;
    });

  // Get unique times for the selected date
  const availableTimes = selectedDate 
    ? Array.from(new Set(filteredAvailabilityByDate.map(slot => slot.startTime))).sort()
    : [];

  // Handle date change
  const handleDateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDate(e.target.value);
    setSelectedTime('');
    setSelectedSlot(null);
  };

  // Handle time change
  const handleTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedTime(e.target.value);
    
    // Find the matching slot
    if (selectedDate) {
      const matchingSlot = availability.find(slot => 
        slot.date === selectedDate && slot.startTime === e.target.value
      );
      
      if (matchingSlot) {
        setSelectedSlot(matchingSlot);
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedProduct || !selectedSlot) {
      setError('Veuillez sélectionner un produit et un créneau horaire');
      return;
    }

    try {
      // Create a more detailed message that includes product, date, and time
      const detailedMessage = `Réservation pour ${selectedProduct.name}
      
Date: ${new Date(selectedSlot.date).toLocaleDateString('fr-FR')}
Heure: ${selectedSlot.startTime}
PArticipants: ${participantsAmount}
     
Commentaire:
${commentaire}`;

      const response = await fetch(API_ENDPOINTS.CONTACT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: selectedProduct.name,
          email,
          object: `Réservation pour ${selectedProduct.name}`,
          telephone,
          message: detailedMessage,
          date: selectedSlot.date,
          time: selectedSlot.startTime,
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi du formulaire');
      }

      alert('Votre réservation a été envoyée avec succès !');
      
      // Reset form
      setEmail('');
      setTelephone('');
      setParticipantsAmount(1);
      setCommentaire('');
      setSelectedProduct(null);
      setSelectedSlot(null);
      setSelectedDate('');
      setSelectedTime('');
    } catch (err) {
      setError('Erreur lors de l\'envoi du formulaire. Veuillez réessayer.');
      console.error('Error submitting form:', err);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <h2>Chargement des produits...</h2>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <h2>Erreur</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>Prendre un rendez-vous</h1>
      
      {!selectedProduct ? (
        // Product selection view
        <>
          <h2>Sélectionnez un produit</h2>
          <p>Choisissez le service que vous souhaitez réserver :</p>
          <div className="product-grid" style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
            {products.map((product) => (
              <ProductCard 
                key={product.id} 
                product={product} 
                onClick={() => handleSelectProduct(product)} 
              />
            ))}
          </div>
        </>
      ) : (
        // Availability selection view
        <>
          <h2>Disponibilités pour {selectedProduct.name}</h2>
          
          <div className="selected-product-info" style={{ marginBottom: '20px', padding: '15px', border: '1px solid #ccc', borderRadius: '5px' }}>
            <p><strong>Prix:</strong> {selectedProduct.price.amount}€</p>
            <p><strong>Durée:</strong> {selectedProduct.duration} {selectedProduct.duration.includes('h') ? 'heure(s)' : 'jour(s)'}</p>
            {selectedProduct.options && selectedProduct.options.length > 0 && (
              <div>
                <p><strong>Options:</strong></p>
                <ul>
                  {selectedProduct.options.map((option, index) => (
                    <li key={index}>{option.name} (+{option.price.amount}€)</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          
          <button onClick={() => setSelectedProduct(null)} className="btn btn-secondary" style={{ marginBottom: '20px' }}>
            Retour aux produits
          </button>
          
          {availability.length === 0 ? (
            <p>Aucune disponibilité disponible pour ce produit.</p>
          ) : (
            <>
              <h3>Choisissez un créneau</h3>
              <p>Sélectionnez une date et heure disponible :</p>
              
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="dateSelect" style={{ display: 'block', marginBottom: '5px' }}>
                  Sélectionnez une date :
                </label>
                <select
                  id="dateSelect"
                  value={selectedDate}
                  onChange={handleDateChange}
                  style={{ padding: '8px', marginRight: '10px', width: '200px' }}
                >
                  <option value="">Choisissez une date</option>
                  {availableDates.map(date => (
                    <option key={date} value={date}>
                      {new Date(date).toLocaleDateString('fr-FR')}
                    </option>
                  ))}
                </select>
              </div>
              
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="timeSelect" style={{ display: 'block', marginBottom: '5px' }}>
                  Sélectionnez une heure :
                </label>
                <select
                  id="timeSelect"
                  value={selectedTime}
                  onChange={handleTimeChange}
                  disabled={!selectedDate}
                  style={{ padding: '8px', width: '200px' }}
                >
                  <option value="">Choisissez une heure</option>
                  {availableTimes.map(time => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
              
              {selectedSlot && (
                <form onSubmit={handleSubmit}>
                  <h3>Vos informations de contact</h3>
                  <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="email" style={{ display: 'block', marginBottom: '5px' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      style={{ padding: '8px', width: '100%', maxWidth: '300px' }}
                    />
                  </div>
                  
                  <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="telephone" style={{ display: 'block', marginBottom: '5px' }}>
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      id="telephone"
                      value={telephone}
                      onChange={(e) => setTelephone(e.target.value)}
                      style={{ padding: '8px', width: '100%', maxWidth: '300px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="participants" style={{ display: 'block', marginBottom: '5px' }}>
                      Nombre de participants :
                    </label>
                    <select
                      id="participants"
                      value={participantsAmount}
                      onChange={(e) => setParticipantsAmount(parseInt(e.target.value))}
                      style={{ padding: '8px', marginRight: '10px', width: '200px' }}
                    >
                      <option value="">Choisissez combien de participants</option>
                      {Array.from({ length: selectedProduct.places }, (_, i) => i + 1).map((i) => (
                        <option key={i} value={i}>
                          {i} participant{i > 1 ? 's' : ''}   
                        </option>
                      ))}
                </select>
              </div>
                  <div style={{ marginBottom: '15px' }}>
                    <label htmlFor="commentaire" style={{ display: 'block', marginBottom: '5px' }}>
                      Commentaire
                    </label>
                    <textarea
                      id="commentaire"
                      value={commentaire}
                      onChange={(e) => setCommentaire(e.target.value)}
                      rows={4}
                      style={{ padding: '8px', width: '100%', maxWidth: '300px' }}
                    />
                  </div>
                  
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    style={{ marginTop: '10px' }}
                  >
                    Envoyer la réservation
                  </button>
                </form>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PriseRdvPage;